/** A Repeat positions sitting stays short enough to finish. */
export const REVIEW_CAP = 8;

/** Overdue and previously difficult decisions first, with variety between courses. */
export function selectReviewSet<
  T extends {
    positionKey: string;
    recommendedSan: string;
    openingId: string;
    due: number;
    lapses: number;
    taskPolicy?: string;
  },
>(cards: readonly T[], now = Date.now()): T[] {
  const unique = new Map<string, T>();
  for (const card of [...cards].sort((a, b) => a.due - b.due)) {
    const key = `${card.positionKey}:${card.taskPolicy || `${card.openingId}:${card.recommendedSan}`}`;
    if (!unique.has(key)) unique.set(key, card);
  }
  const remaining = [...unique.values()];
  const selected: T[] = [];
  while (remaining.length && selected.length < REVIEW_CAP) {
    const last = selected.at(-1)?.openingId;
    const score = (card: T) =>
      (now - card.due) / 86_400_000 +
      Math.min(5, card.lapses) * 2 -
      (card.openingId === last ? 1 : 0);
    remaining.sort((a, b) => score(b) - score(a));
    selected.push(remaining.shift()!);
  }
  return selected;
}

export function reviewTimeLabel(due: number, now = Date.now()): string {
  const date = new Date(due);
  const today = new Date(now);
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const sameDate = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (due <= now) return "now";
  if (sameDate(date, today))
    return `today at ${date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
  if (sameDate(date, tomorrow)) return "tomorrow";
  return `on ${date.toLocaleDateString([], { month: "short", day: "numeric" })}`;
}

/** "Tomorrow" with a little slack for a card saved late in the day. */
const SOON_MS = 36 * 60 * 60 * 1000;

/**
 * Break the order a line was learned in, so the previous answer is not a cue.
 * One or two cards stay in store order: callers and tests that set up a pair
 * still meet the first card first.
 */
export function mixReviewOrder<T>(items: readonly T[]): T[] {
  if (items.length <= 2) return [...items];
  const mid = Math.floor(items.length / 2);
  const rotated = [...items.slice(mid), ...items.slice(0, mid)];
  const next: T[] = [];
  const copy = [...rotated];
  while (copy.length) {
    const first = copy.shift();
    if (first !== undefined) next.push(first);
    const last = copy.pop();
    if (last !== undefined) next.push(last);
  }
  return next;
}

export function soonCount(
  cards: readonly { due: number; openingId?: string }[],
  now: number,
  openingId?: string
): number {
  return cards.filter(
    (card) =>
      (openingId ? card.openingId === openingId : true) &&
      card.due > now &&
      card.due - now <= SOON_MS
  ).length;
}
